import React, { useState, useEffect } from 'react';
import { collection, getDocs, updateDoc, doc, query, where, writeBatch } from 'firebase/firestore';
import { db } from '../../firebaseConfig';
import Swal from 'sweetalert2';

interface Property {
  id: string;
  nombre: string;
  ciudad?: string;
  codigo_zip?: string;
  tipo?: string;
  inmobiliaria?: string;
  agente_id?: string;
  agente_nombre?: string;
  precio?: number;
  recamaras?: number;
  baños?: number;
}

interface Agency {
  id: string;
  nombre: string;
  ciudad?: string;
  correo?: string;
  telefono?: string;
}

interface Agent {
  id: string;
  nombre: string;
  correo: string;
  inmobiliaria?: string;
  tipo?: string;
  telefono?: string;
}

const PropertyManualLinker: React.FC = () => {
  const [properties, setProperties] = useState<Property[]>([]);
  const [filteredProperties, setFilteredProperties] = useState<Property[]>([]);
  const [agencies, setAgencies] = useState<Agency[]>([]);
  const [agents, setAgents] = useState<Agent[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedProperties, setSelectedProperties] = useState<Set<string>>(new Set());
  const [selectedAgency, setSelectedAgency] = useState<Agency | null>(null);
  const [agencyAgents, setAgencyAgents] = useState<Agent[]>([]);
  const [selectedAgent, setSelectedAgent] = useState<Agent | null>(null);
  const [successCount, setSuccessCount] = useState(0);

  // Filtros
  const [filters, setFilters] = useState({
    searchTerm: '',
    ciudad: '',
    tipo: '',
    hasAgency: 'without' as 'all' | 'with' | 'without'
  });

  const [agencySearch, setAgencySearch] = useState('');
  const [agentSearch, setAgentSearch] = useState('');
  const [tab, setTab] = useState<'properties' | 'link'>('properties');
  const [processing, setProcessing] = useState(false);

  // Cargar datos iniciales
  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      // Cargar todas las propiedades
      const propsSnapshot = await getDocs(collection(db, 'propiedades'));
      const props: Property[] = [];

      propsSnapshot.forEach(doc => {
        const data = doc.data();
        props.push({
          id: doc.id,
          nombre: data.nombre || 'Sin nombre',
          ciudad: data.ciudad,
          codigo_zip: data.codigo_zip,
          tipo: data.tipo,
          inmobiliaria: data.inmobiliaria,
          agente_id: data.agente_id,
          agente_nombre: data.agente_nombre,
          precio: data.precio,
          recamaras: data.recamaras,
          baños: data.baños
        });
      });

      // Cargar todas las agencias
      const agenciesSnapshot = await getDocs(collection(db, 'inmobiliarias'));
      const agenciesList: Agency[] = [];
      agenciesSnapshot.forEach(doc => {
        const data = doc.data();
        agenciesList.push({
          id: doc.id,
          nombre: data.nombre,
          ciudad: data.ciudad,
          correo: data.correo,
          telefono: data.telefono
        });
      });

      // Cargar todos los agentes
      const afiliadosSnapshot = await getDocs(collection(db, 'asesor'));
      const asociadosSnapshot = await getDocs(collection(db, 'asociado'));
      const agentsList: Agent[] = [];

      afiliadosSnapshot.forEach(doc => {
        const data = doc.data();
        agentsList.push({
          id: doc.id,
          nombre: data.nombre,
          correo: data.correo,
          inmobiliaria: data.inmobiliaria,
          tipo: 'Afiliado',
          telefono: data.telefono
        });
      });

      asociadosSnapshot.forEach(doc => {
        const data = doc.data();
        agentsList.push({
          id: doc.id,
          nombre: data.nombre,
          correo: data.correo,
          inmobiliaria: data.inmobiliaria,
          tipo: 'Asociado',
          telefono: data.telefono
        });
      });

      setProperties(props);
      setFilteredProperties(props);
      setAgencies(agenciesList.sort((a, b) => a.nombre.localeCompare(b.nombre)));
      setAgents(agentsList.sort((a, b) => a.nombre.localeCompare(b.nombre)));

      if (process.env.NODE_ENV === "development") console.log(`Cargados: ${props.length} propiedades, ${agenciesList.length} agencias, ${agentsList.length} agentes`);
    } catch (error) {
      console.error('Error cargando datos:', error);
    } finally {
      setLoading(false);
    }
  };

  // Filtrar propiedades
  useEffect(() => {
    let filtered = properties;

    // Filtro por búsqueda
    if (filters.searchTerm) {
      filtered = filtered.filter(p =>
        p.nombre.toLowerCase().includes(filters.searchTerm.toLowerCase()) ||
        (p.ciudad && p.ciudad.toLowerCase().includes(filters.searchTerm.toLowerCase())) ||
        (p.codigo_zip && p.codigo_zip.includes(filters.searchTerm))
      );
    }

    // Filtro por ciudad
    if (filters.ciudad) {
      filtered = filtered.filter(p => p.ciudad === filters.ciudad);
    }

    // Filtro por tipo
    if (filters.tipo) {
      filtered = filtered.filter(p => p.tipo === filters.tipo);
    }

    // Filtro por agencia
    if (filters.hasAgency === 'with') {
      filtered = filtered.filter(p => p.inmobiliaria && p.inmobiliaria !== '');
    } else if (filters.hasAgency === 'without') {
      filtered = filtered.filter(p => !p.inmobiliaria || p.inmobiliaria === '');
    }

    setFilteredProperties(filtered);
  }, [filters, properties]);

  // Cargar agentes de la agencia seleccionada
  useEffect(() => {
    if (selectedAgency) {
      const agencyAgentsList = agents.filter(a => a.inmobiliaria === selectedAgency.id);
      setAgencyAgents(agencyAgentsList);
    } else {
      setAgencyAgents([]);
    }
  }, [selectedAgency, agents]);

  // Seleccionar/deseleccionar propiedad
  const togglePropertySelection = (propertyId: string) => {
    const newSelected = new Set(selectedProperties);
    if (newSelected.has(propertyId)) {
      newSelected.delete(propertyId);
    } else {
      newSelected.add(propertyId);
    }
    setSelectedProperties(newSelected);
  };

  // Seleccionar todas las propiedades visibles
  const selectAll = () => {
    const newSelected = new Set(filteredProperties.map(p => p.id));
    setSelectedProperties(newSelected);
  };

  // Deseleccionar todas
  const deselectAll = () => {
    setSelectedProperties(new Set());
  };

  // Vincular propiedades seleccionadas
  const linkSelectedProperties = async () => {
    if (selectedProperties.size === 0) {
      Swal.fire({
        icon: 'warning',
        title: 'Selección requerida',
        text: 'Selecciona al menos una propiedad',
        confirmButtonColor: '#6c757d'
      });
      return;
    }

    if (!selectedAgency) {
      Swal.fire({
        icon: 'warning',
        title: 'Agencia requerida',
        text: 'Selecciona una agencia',
        confirmButtonColor: '#6c757d'
      });
      return;
    }

    const confirmMsg = selectedAgent
      ? `¿Vincular ${selectedProperties.size} propiedades con ${selectedAgency.nombre} y el agente ${selectedAgent.nombre}?`
      : `¿Vincular ${selectedProperties.size} propiedades con ${selectedAgency.nombre}?`;

    const result = await Swal.fire({
      icon: 'question',
      title: 'Confirmar vinculación',
      text: confirmMsg,
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Sí, vincular',
      cancelButtonText: 'Cancelar'
    });

    if (!result.isConfirmed) return;

    setProcessing(true);
    const batch = writeBatch(db);
    let count = 0;

    try {
      selectedProperties.forEach(propId => {
        const propRef = doc(db, 'propiedades', propId);
        const updateData: any = {
          inmobiliaria: selectedAgency.id
        };

        if (selectedAgent) {
          updateData.agente_id = selectedAgent.correo;
          updateData.agente_nombre = selectedAgent.nombre;
        }

        batch.update(propRef, updateData);
        count++;
      });

      await batch.commit();

      // Actualizar estado local
      setProperties(prev => prev.map(p => {
        if (selectedProperties.has(p.id)) {
          return {
            ...p,
            inmobiliaria: selectedAgency.id,
            agente_id: selectedAgent?.correo,
            agente_nombre: selectedAgent?.nombre
          };
        }
        return p;
      }));

      setSuccessCount(prev => prev + count);
      setSelectedProperties(new Set());
      setSelectedAgency(null);
      setSelectedAgent(null);
      Swal.fire({
        icon: 'success',
        title: '¡Éxito!',
        text: `${count} propiedades vinculadas exitosamente`,
        confirmButtonColor: '#3085d6'
      });
    } catch (error) {
      console.error('Error vinculando propiedades:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'No se pudieron vincular las propiedades',
        confirmButtonColor: '#d33'
      });
    } finally {
      setProcessing(false);
    }
  };

  // Obtener estadísticas
  const getStats = () => {
    const withAgency = properties.filter(p => p.inmobiliaria && p.inmobiliaria !== '').length;
    const withoutAgency = properties.filter(p => !p.inmobiliaria || p.inmobiliaria === '').length;
    const withAgent = properties.filter(p => p.agente_id && p.agente_id !== '').length;

    return { total: properties.length, withAgency, withoutAgency, withAgent };
  };

  const stats = getStats();

  // Obtener ciudades únicas
  const getCiudadesUnicas = () => {
    const ciudades = new Set<string>();
    properties.forEach(p => {
      if (p.ciudad) ciudades.add(p.ciudad);
    });
    return Array.from(ciudades).sort();
  };

  // Obtener tipos únicos
  const getTiposUnicos = () => {
    const tipos = new Set<string>();
    properties.forEach(p => {
      if (p.tipo) tipos.add(p.tipo);
    });
    return Array.from(tipos).sort();
  };

  // Filtrar agencias por búsqueda
  const getFilteredAgencies = () => {
    if (!agencySearch) return agencies;
    return agencies.filter(a =>
      a.nombre.toLowerCase().includes(agencySearch.toLowerCase())
    );
  };

  // Filtrar agentes por búsqueda
  const getFilteredAgents = () => {
    if (!agentSearch) return agencyAgents;
    return agencyAgents.filter(a =>
      a.nombre.toLowerCase().includes(agentSearch.toLowerCase()) ||
      a.correo.toLowerCase().includes(agentSearch.toLowerCase())
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Cargando datos...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 py-8">
      <div className="max-w-7xl mx-auto px-4">
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-600 to-purple-600 p-6">
            <h1 className="text-3xl font-bold text-white flex items-center gap-3">
              🏢 Gestor de Asignación de Propiedades
            </h1>
            <p className="text-blue-100 mt-2">
              Vincule propiedades con agencias y agentes de forma eficiente
            </p>
          </div>

          {/* Estadísticas */}
          <div className="p-6 bg-gray-50 border-b">
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-200">
                <p className="text-3xl font-bold text-gray-800">{stats.total}</p>
                <p className="text-sm text-gray-600">Total Propiedades</p>
              </div>
              <div className="bg-green-50 p-4 rounded-xl shadow-sm border border-green-200">
                <p className="text-3xl font-bold text-green-600">{stats.withAgency}</p>
                <p className="text-sm text-green-800">Con Agencia</p>
              </div>
              <div className="bg-red-50 p-4 rounded-xl shadow-sm border border-red-200">
                <p className="text-3xl font-bold text-red-600">{stats.withoutAgency}</p>
                <p className="text-sm text-red-800">Sin Agencia</p>
              </div>
              <div className="bg-purple-50 p-4 rounded-xl shadow-sm border border-purple-200">
                <p className="text-3xl font-bold text-purple-600">{stats.withAgent}</p>
                <p className="text-sm text-purple-800">Con Agente</p>
              </div>
              <div className="bg-blue-50 p-4 rounded-xl shadow-sm border border-blue-200">
                <p className="text-3xl font-bold text-blue-600">{successCount}</p>
                <p className="text-sm text-blue-800">Vinculadas Hoy</p>
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div className="border-b">
            <div className="flex">
              <button
                onClick={() => setTab('properties')}
                className={`px-6 py-3 font-semibold transition-colors ${
                  tab === 'properties'
                    ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50'
                    : 'text-gray-600 hover:text-gray-800'
                }`}
              >
                📋 Seleccionar Propiedades ({selectedProperties.size})
              </button>
              <button
                onClick={() => setTab('link')}
                className={`px-6 py-3 font-semibold transition-colors ${
                  tab === 'link'
                    ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50'
                    : 'text-gray-600 hover:text-gray-800'
                }`}
              >
                🔗 Vincular con Agencia
              </button>
            </div>
          </div>

          {/* Contenido */}
          <div className="p-6">
            {tab === 'properties' ? (
              <div>
                {/* Filtros */}
                <div className="bg-gray-50 rounded-xl p-4 mb-6">
                  <h3 className="font-semibold mb-3 text-gray-700">Filtros</h3>
                  <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-600 mb-1">
                        Buscar
                      </label>
                      <input
                        type="text"
                        value={filters.searchTerm}
                        onChange={(e) => setFilters({...filters, searchTerm: e.target.value})}
                        placeholder="Nombre, ciudad..."
                        className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-600 mb-1">
                        Ciudad
                      </label>
                      <select
                        value={filters.ciudad}
                        onChange={(e) => setFilters({...filters, ciudad: e.target.value})}
                        className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="">Todas</option>
                        {getCiudadesUnicas().map(ciudad => (
                          <option key={ciudad} value={ciudad}>{ciudad}</option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-600 mb-1">
                        Tipo
                      </label>
                      <select
                        value={filters.tipo}
                        onChange={(e) => setFilters({...filters, tipo: e.target.value})}
                        className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="">Todos</option>
                        {getTiposUnicos().map(tipo => (
                          <option key={tipo} value={tipo}>{tipo}</option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-600 mb-1">
                        Estado
                      </label>
                      <select
                        value={filters.hasAgency}
                        onChange={(e) => setFilters({...filters, hasAgency: e.target.value as any})}
                        className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="all">Todas</option>
                        <option value="without">Sin Agencia</option>
                        <option value="with">Con Agencia</option>
                      </select>
                    </div>

                    <div className="flex items-end gap-2">
                      <button
                        onClick={selectAll}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                      >
                        Seleccionar Todo
                      </button>
                      <button
                        onClick={deselectAll}
                        className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
                      >
                        Limpiar
                      </button>
                    </div>
                  </div>
                </div>

                {/* Lista de propiedades */}
                <div className="bg-gray-50 rounded-xl p-4">
                  <div className="flex justify-between items-center mb-3">
                    <h3 className="font-semibold text-gray-700">
                      Propiedades ({filteredProperties.length})
                    </h3>
                    <span className="text-sm text-gray-600">
                      {selectedProperties.size} seleccionadas
                    </span>
                  </div>

                  <div className="max-h-96 overflow-y-auto space-y-2">
                    {filteredProperties.map(prop => (
                      <div
                        key={prop.id}
                        onClick={() => togglePropertySelection(prop.id)}
                        className={`p-4 rounded-lg cursor-pointer transition-all ${
                          selectedProperties.has(prop.id)
                            ? 'bg-blue-100 border-2 border-blue-400 shadow-md'
                            : 'bg-white border border-gray-200 hover:border-gray-300 hover:shadow-sm'
                        }`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <p className="font-semibold text-gray-800">
                              {prop.nombre}
                            </p>
                            <div className="flex flex-wrap gap-2 mt-1">
                              {prop.ciudad && (
                                <span className="text-xs bg-gray-100 px-2 py-1 rounded">
                                  📍 {prop.ciudad}
                                </span>
                              )}
                              {prop.tipo && (
                                <span className="text-xs bg-purple-100 px-2 py-1 rounded">
                                  🏠 {prop.tipo}
                                </span>
                              )}
                              {prop.precio && (
                                <span className="text-xs bg-green-100 px-2 py-1 rounded">
                                  💰 ${prop.precio.toLocaleString()}
                                </span>
                              )}
                              {prop.recamaras && (
                                <span className="text-xs bg-blue-100 px-2 py-1 rounded">
                                  🛏️ {prop.recamaras} rec
                                </span>
                              )}
                            </div>
                            {prop.inmobiliaria && (
                              <p className="text-xs text-green-600 mt-1">
                                ✅ Agencia: {agencies.find(a => a.id === prop.inmobiliaria)?.nombre}
                              </p>
                            )}
                            {prop.agente_nombre && (
                              <p className="text-xs text-blue-600">
                                👤 Agente: {prop.agente_nombre}
                              </p>
                            )}
                          </div>
                          <div className="ml-3">
                            <input
                              type="checkbox"
                              checked={selectedProperties.has(prop.id)}
                              onChange={() => {}}
                              className="w-5 h-5 text-blue-600"
                              onClick={(e) => e.stopPropagation()}
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <div>
                {/* Panel de vinculación */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Selección de Agencia */}
                  <div>
                    <h3 className="font-semibold text-gray-700 mb-4">
                      Seleccionar Agencia
                    </h3>

                    <div className="mb-4">
                      <input
                        type="text"
                        value={agencySearch}
                        onChange={(e) => setAgencySearch(e.target.value)}
                        placeholder="Buscar agencia por nombre..."
                        className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                      />
                    </div>

                    <div className="bg-gray-50 rounded-xl p-4 max-h-96 overflow-y-auto">
                      <div className="space-y-2">
                        {getFilteredAgencies().map(agency => (
                          <div
                            key={agency.id}
                            onClick={() => setSelectedAgency(agency)}
                            className={`p-4 rounded-lg cursor-pointer transition-all ${
                              selectedAgency?.id === agency.id
                                ? 'bg-blue-100 border-2 border-blue-400 shadow-md'
                                : 'bg-white border border-gray-200 hover:border-gray-300 hover:shadow-sm'
                            }`}
                          >
                            <p className="font-semibold text-gray-800">
                              {agency.nombre}
                            </p>
                            <div className="text-xs text-gray-600 mt-1">
                              {agency.ciudad && <span>📍 {agency.ciudad}</span>}
                              {agency.correo && <span className="ml-2">✉️ {agency.correo}</span>}
                            </div>
                            <p className="text-xs text-blue-600 mt-1">
                              👥 {agents.filter(a => a.inmobiliaria === agency.id).length} agentes
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Selección de Agente */}
                  <div>
                    <h3 className="font-semibold text-gray-700 mb-4">
                      Seleccionar Agente (Opcional)
                    </h3>

                    {selectedAgency ? (
                      <>
                        <div className="mb-4">
                          <input
                            type="text"
                            value={agentSearch}
                            onChange={(e) => setAgentSearch(e.target.value)}
                            placeholder="Buscar agente por nombre o correo..."
                            className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                          />
                        </div>

                        <div className="bg-gray-50 rounded-xl p-4 max-h-96 overflow-y-auto">
                          {getFilteredAgents().length > 0 ? (
                            <div className="space-y-2">
                              <div
                                onClick={() => setSelectedAgent(null)}
                                className={`p-4 rounded-lg cursor-pointer transition-all ${
                                  selectedAgent === null
                                    ? 'bg-orange-100 border-2 border-orange-400 shadow-md'
                                    : 'bg-white border border-gray-200 hover:border-gray-300 hover:shadow-sm'
                                }`}
                              >
                                <p className="font-semibold text-gray-800">
                                  Sin agente específico
                                </p>
                                <p className="text-xs text-gray-600">
                                  Solo asignar la agencia
                                </p>
                              </div>

                              {getFilteredAgents().map(agent => (
                                <div
                                  key={agent.id}
                                  onClick={() => setSelectedAgent(agent)}
                                  className={`p-4 rounded-lg cursor-pointer transition-all ${
                                    selectedAgent?.id === agent.id
                                      ? 'bg-blue-100 border-2 border-blue-400 shadow-md'
                                      : 'bg-white border border-gray-200 hover:border-gray-300 hover:shadow-sm'
                                  }`}
                                >
                                  <p className="font-semibold text-gray-800">
                                    {agent.nombre}
                                  </p>
                                  <div className="text-xs text-gray-600 mt-1">
                                    <span className="inline-block bg-purple-100 px-2 py-1 rounded mr-2">
                                      {agent.tipo}
                                    </span>
                                    <span>✉️ {agent.correo}</span>
                                    {agent.telefono && <span className="ml-2">📱 {agent.telefono}</span>}
                                  </div>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <p className="text-center text-gray-500 py-4">
                              No hay agentes en esta agencia
                            </p>
                          )}
                        </div>
                      </>
                    ) : (
                      <div className="bg-gray-50 rounded-xl p-8 text-center text-gray-500">
                        <p>Selecciona primero una agencia</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Resumen y botón de vincular */}
                <div className="mt-6 bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl p-6">
                  <h3 className="font-semibold text-gray-700 mb-4">Resumen de Vinculación</h3>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                    <div className="bg-white rounded-lg p-4 shadow-sm">
                      <p className="text-sm text-gray-600">Propiedades seleccionadas</p>
                      <p className="text-2xl font-bold text-blue-600">{selectedProperties.size}</p>
                    </div>

                    <div className="bg-white rounded-lg p-4 shadow-sm">
                      <p className="text-sm text-gray-600">Agencia</p>
                      <p className="font-semibold text-gray-800">
                        {selectedAgency ? selectedAgency.nombre : 'No seleccionada'}
                      </p>
                    </div>

                    <div className="bg-white rounded-lg p-4 shadow-sm">
                      <p className="text-sm text-gray-600">Agente</p>
                      <p className="font-semibold text-gray-800">
                        {selectedAgent ? selectedAgent.nombre : 'Sin agente'}
                      </p>
                    </div>
                  </div>

                  <button
                    onClick={linkSelectedProperties}
                    disabled={selectedProperties.size === 0 || !selectedAgency || processing}
                    className={`w-full py-3 rounded-lg font-semibold transition-all ${
                      selectedProperties.size > 0 && selectedAgency && !processing
                        ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white hover:from-blue-700 hover:to-purple-700 shadow-lg'
                        : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    }`}
                  >
                    {processing ? (
                      <span className="flex items-center justify-center">
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                        Procesando...
                      </span>
                    ) : (
                      `🔗 Vincular ${selectedProperties.size} Propiedades`
                    )}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PropertyManualLinker;